<template>
  <div
    id="invitationList"
    class="mainCard"
  >
    <div class="filter">
      <div
        class="title"
        :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
      >
        {{ $t('oa.leader.invitation') }}
      </div>

      <div
        class="titleOptions"
        :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
      >
        <el-input
          v-model="filterText"
          class="filterText"
          :placeholder="$t('common.search')"
          :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
          @change="loadInvitation('change')"
        >
          <el-button
            slot="append"
            icon="el-icon-search"
            @click="loadInvitation"
          />
        </el-input>
      </div>
    </div>

    <el-table
      ref="invitationTable"
      v-loading="loading"
      :data="users"
      style="width: 100%;"
      class="usersList clear-table"
      :empty-text="$t('oa.leader.noInvitation')"
      @sort-change="sortChange"
    >
      <el-table-column
        prop="email"
        :label="$t('common.email')"
        class="firstCol"
        :width="250"
      />
      <el-table-column
        prop="is_verified"
        :label="$t('oa.leader.checkedEmail')"
        sortable="custom"
      >
        <template slot-scope="scope">
          <span style="text-transform: capitalize;">
            {{ scope.row.is_verified }}
          </span>
        </template>
      </el-table-column>

      <el-table-column
        prop="created_at"
        :label="$t('oa.leader.createdDate')"
        sortable="custom"
      />

      <el-table-column
        prop="expires_in"
        :label="$t('oa.leader.expiresIn')"
      >
        <template slot-scope="scope">
          {{ `${scope.row.expire_d_day
            ? scope.row.expire_d_day : '0'} ${$t('oa.leader.daysLeft')}` }}
        </template>
      </el-table-column>

      <el-table-column
        prop="button"
        width="90"
      >
        <template slot-scope="scope">
          <el-tooltip
            class="item"
            effect="dark"
            :content="$t('oa.leader.resendInvitationTooltip')"
            placement="top"
          >
            <el-button
              class="invitationDeleteBtn"
              icon="el-icon-message"
              size="mini"
              circle
              :disabled="processResend"
              :loading="processResend"
              @click.stop="confirmResend(scope.row.pk)"
            />
          </el-tooltip>
          <el-tooltip
            class="item"
            effect="dark"
            :content="$t('oa.leader.deleteInvitationTooltip')"
            placement="top"
          >
            <el-button
              class="invitationDeleteBtn"
              icon="el-icon-delete"
              size="mini"
              circle
              :disabled="processDelete"
              @click.stop="confirmDelete(scope.row.pk)"
            />
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <div
      v-if="team"
      class="addUserBtn"
      @click="openModal('leader')"
    >
      + {{ $t('oa.leader.inviteNewLeader') }}
    </div>

    <AddTeamUser
      :show.sync="addUserModal"
      :role="addUserRole"
      :team="team"
      @newUsers="addUser"
    />

    <el-pagination
      class="filterPagination"
      layout="prev, pager, next"
      :total="count"
      :pager-count="5"
      :current-page.sync="currentPage"
      @current-change="loadInvitation"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import api from '@/api';
import AddTeamUser from './AddTeamUser.vue';

export default {
  components: {
    AddTeamUser,
  },

  props: {
    team: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      filterText: '',
      filterSelect: 'search',
      sortField: '',
      sortOrder: '',
      users: [],
      currentPage: 1,
      count: 0,
      loading: false,
      processResend: false,
      processDelete: false,
      addUserModal: false,
      addUserRole: 'leader',
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),
  },

  created() {
    this.loadInvitation();
  },

  methods: {
    async loadInvitation(status) {
      try {
        this.loading = true;
        const teamId = this.team.uuid;
        let response = {};
        const param = {};

        if (this.user && this.user.organization && this.user.organization.uuid) {
          param.org_uuid = this.user.organization.uuid;
        }

        if (status === 'change') {
          this.currentPage = 1;
          param.page = this.currentPage;
        } else {
          param.page = this.currentPage;
        }

        if (this.filterSelect && this.filterText) {
          param[this.filterSelect] = this.filterText;
        }

        if (this.sortField && this.sortOrder) {
          param.sort_field = this.sortField;
          param.sort_order = this.sortOrder;
        }

        response = await api.getTeamInvitation(teamId, param);
        this.users = response.data.results;
        this.count = response.data.count;
        this.loading = false;
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
        }
        this.loading = false;
      }
    },

    sortChange({ prop, order }) {
      this.sortField = prop;
      switch (order) {
        case 'descending':
          this.sortOrder = 'DESC';
          break;
        case 'ascending':
          this.sortOrder = 'ASC';
          break;
        default:
          this.sortField = null;
          this.sortOrder = null;
      }

      this.loadInvitation();
    },

    async confirmDelete(pk) {
      this.$alert(this.$t('oa.leader.confirmDeleteInvitation'), '', {
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.goBack'),
      }).then(() => {
        this.deleteInvitation(pk);
      }).catch(() => {});
    },

    async confirmResend(pk) {
      this.$alert(this.$t('oa.leader.confirmResendInvitation'), '', {
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.goBack'),
      }).then(() => {
        this.resendEmail(pk);
      }).catch(() => {});
    },

    async resendEmail(pk) {
      try {
        this.processResend = true;
        await api.resendLeaderInvitationOa(this.team.uuid, pk);
        this.$message.success(this.$t('oa.leader.invitationEmailResent'));
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.processResend = false;
      }
    },

    async deleteInvitation(pk) {
      try {
        this.processDelete = true;
        await api.deleteTeamInvitation(this.team.uuid, pk);
        this.$message.success(this.$t('oa.leader.invitationDeleted'));
        this.loadInvitation();
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.processDelete = false;
      }
    },

    openModal(role) {
      this.addUserModal = true;
      this.addUserRole = role;
    },

    addUser(users) {
      const u = users.map((el) => {
        const e = el;
        if (!e.status) e.status = 'pending';
        return e;
      });
      this.users.unshift(...u);
    },
  },
};
</script>

<style lang="scss" scoped>
  #invitationList {
    .filter {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
      margin: 0 auto 10px;

      .title {
        color: var(--themeFontColor);
        font-size: 20px;
        font-weight: bold;

        &.mobileMode {
          width: 100%;
        }
      }

      .titleOptions {
        display: flex;
        align-items: center;

        .filterText {
          height: 30px;
          width: 250px;
          border-bottom: 1px solid var(--themeFontColor);
          text-align: right;

          &.mobileMode {
            width: 100%;
          }
        }

        &.mobileMode {
          width: 100%;
        }
      }
    }

    .invitationDeleteBtn {
      background: transparent;
      border-width: 0;
      color: var(--themeFontColor);

      &:hover { background: rgba(255, 255, 255, 0.2); }
      &:active { background: rgba(255, 255, 255, 0.5); }
    }

    .addUserBtn {
      background-color: transparent;
      border: 1px dashed var(--themeFontColor);
      color: var(--themeFontColor);
      cursor: pointer;
      margin-top: 15px;
      height: 68px;
      line-height: 68px;
      border-radius: 10px;
      text-align: center;

      &:hover {
        background-color: rgba(255, 255, 255, 0.3);
      }
    }
  }
</style>

<style lang="scss">
  #invitationList {
    .el-table__expand-icon {
      color: var(--themeFontColor);
    }

    .filter {
      .filterText {
        input {
          background-color: transparent;
          color: var(--themeFontColor);
          border: none;
          border-radius: 0;
          padding: 0 2px;
        }

        .el-input-group__append {
          background-color: transparent;
          border: none;
          color: var(--themeFontColor);
          font-size: 16px;
          padding: 0 20px 0 5px;

          .el-button {
            padding: 5px;
          }
        }
      }

      .el-select {
        width: 150px;
        max-width: 40%;

        .el-input {
          input {
            border-radius: 20px;
            background-color: transparent;
            color: var(--themeFontColor);
          }
        }
      }
    }

    .usersList {
      .el-table__header {
        tr {
          th:first-child {
            padding-left: 15px;
          }
        }
      }

      .el-table__row {
        td:first-child {
          padding-left: 15px;
        }
      }
    }
  }
</style>
